import { useMemo, useState } from "react";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

const Totales = () => {
  const { token } = useAuth();

  const [dataCadetes, setDataCadetes] = useState([]);
  const [dataClientes, setDataCientes] = useState([]);

  const dataRequest = async (e) => {
    e.preventDefault();

    if (!token) {
      alert("❌ Sesión inválida. Volvé a iniciar sesión.");
      return;
    }

    try {
      const res = await authFetch("/v1/data/admin", {
        method: "GET",
        token,
      });

      const unfiltered = await res.json();

      if (!res.ok) {
        throw new Error(unfiltered?.error || "Error trayendo totales");
      }

      setDataCadetes(unfiltered.resumen_cadetes || []);
      setDataCientes(unfiltered.resumen_clientes || []);

      alert("✅ MOSTRANDO DATOS");
    } catch (err) {
      console.error(err);
      alert("❌ ERROR TRAYENDO DATOS");
    }
  };

  const resetDatabase = async () => {
    const confirmed = window.confirm(
      "¿Seguro quiere borrar la DB? Esta acción reinicia la base legacy y no se puede deshacer."
    );

    if (!confirmed) return;

    if (!token) {
      alert("❌ Sesión inválida. Volvé a iniciar sesión.");
      return;
    }

    try {
      const res = await authFetch("/v1/utils/reset-db", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({}),
      });

      const payload = await res.json();

      if (!res.ok) {
        throw new Error(
          payload?.error || payload?.message || "Error al reiniciar la base de datos"
        );
      }

      alert(payload?.message || "🧹 Base de datos reiniciada con éxito");
      setDataCadetes([]);
      setDataCientes([]);
    } catch (err) {
      console.error("❌ resetDatabase:", err);
      alert(
        `❌ Error reiniciando la base de datos: ${
          err instanceof Error ? err.message : "Error desconocido"
        }`
      );
    }
  };

  const handleDownloadExcel = async () => {
    if (!dataCadetes.length && !dataClientes.length) {
      alert("No hay datos para exportar");
      return;
    }

    const workbook = new ExcelJS.Workbook();

    const pastelHeader = "FFDEF7E5";
    const pastelBody = "FFF0F4F8";
    const dorado = "FFFFC300";
    const negro = "FF000000";
    const blanco = "FFFFFFFF";

    function formatColumnByHeader(sheet, headerText, format) {
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        if (cell.value === headerText) {
          sheet.getColumn(colNumber).eachCell((c, rowNumber) => {
            if (rowNumber > 1 && typeof c.value === "number") {
              c.numFmt = format;
            }
          });
        }
      });
    }

    const addSheet = (title, data) => {
      const ws = workbook.addWorksheet(title);

      if (!data.length) return;

      const headers = Object.keys(data[0]);

      const headerRow = ws.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: negro } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: pastelHeader },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      data.forEach((row) => {
        const values = headers.map((h) => {
          if (h === "semanal" || h === "parcial" || h === "%") {
            return Number((row[h] || "0").toString().replace(",", "."));
          }
          return row[h];
        });

        const r = ws.addRow(values);

        r.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: pastelBody },
          };
          cell.font = { color: { argb: negro } };
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      ws.columns.forEach((col) => {
        let max = 10;
        col.eachCell?.((cell) => {
          const len = cell.value?.toString().length || 0;
          if (len > max) max = len;
        });
        col.width = max + 2;
      });

      ws.views = [{ state: "frozen", ySplit: 1 }];

      const hasSemanal = headers.includes("semanal");
      const hasParcial = headers.includes("parcial");

      if (hasSemanal || hasParcial) {
        const totalSemanal = hasSemanal
          ? data.reduce(
              (acc, r) =>
                acc + Number((r.semanal || "0").toString().replace(",", ".")),
              0
            )
          : null;

        const totalParcial = hasParcial
          ? data.reduce(
              (acc, r) =>
                acc + Number((r.parcial || "0").toString().replace(",", ".")),
              0
            )
          : null;

        const totalsRowValues = headers.map((h) => {
          if (h === "semanal") return totalSemanal?.toFixed(2);
          if (h === "parcial") return totalParcial?.toFixed(2);
          if (h === headers[0]) return "TOTAL";
          return "";
        });

        const totalsRow = ws.addRow(totalsRowValues);

        totalsRow.eachCell((cell, colNumber) => {
          const isNumberCol =
            headers[colNumber - 1] === "semanal" ||
            headers[colNumber - 1] === "parcial";

          cell.font = {
            bold: isNumberCol || colNumber === 1,
            color: { argb: isNumberCol ? dorado : negro },
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: blanco },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: isNumberCol ? "right" : "left",
          };
        });
      }

      if (hasSemanal) formatColumnByHeader(ws, "semanal", '"$"#,##0.00');
      if (hasParcial) formatColumnByHeader(ws, "parcial", '"$"#,##0.00');
      formatColumnByHeader(ws, "%", '0.00"%"');
    };

    addSheet("Cadetes", dataCadetes);
    addSheet("Clientes", dataClientes);

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `totales_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
  };

  const handleDownloadAllClients = async () => {
    if (!token) {
      alert("❌ Sesión inválida. Volvé a iniciar sesión.");
      return;
    }

    if (!dataClientes.length) return alert("No hay datos para exportar");

    const zip = new JSZip();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const dd = String(yesterday.getDate()).padStart(2, "0");
    const mm = String(yesterday.getMonth() + 1).padStart(2, "0");
    const yyyy = yesterday.getFullYear();
    const formattedDate = `${dd}-${mm}-${yyyy}`;

    for (const cliente of dataClientes) {
      const nombre = cliente.cliente;

      try {
        const res = await authFetch("/v1/data/client/me", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({ username: nombre }),
        });

        const payload = await res.json();

        if (!res.ok || !payload.result?.length) {
          console.warn(`⚠️ Sin datos para: ${nombre}`);
          continue;
        }

        const rows = payload.result;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Envios");

        const pastelHeader = "FFDEF7E5";
        const pastelBody = "FFF0F4F8";
        const dorado = "FFFFC300";
        const blanco = "FFFFFFFF";
        const negro = "FF000000";

        function formatColumnByHeader(sheet, headerText, format) {
          const headerRow = sheet.getRow(1);
          headerRow.eachCell((cell, colNumber) => {
            if (cell.value === headerText) {
              sheet.getColumn(colNumber).eachCell((c, rowNumber) => {
                if (rowNumber > 1 && typeof c.value === "number") {
                  c.numFmt = format;
                }
              });
            }
          });
        }

        const headers = Object.keys(rows[0]);
        const headersRow = sheet.addRow(headers);

        headersRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: negro } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: pastelHeader },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        rows.forEach((row) => {
          const rowData = headers.map((h) => {
            if (h === "precio_cliente") return Number(row[h]);
            if (h === "descuento") return Number(row[h]);
            return row[h];
          });

          const newRow = sheet.addRow(rowData);

          newRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: pastelBody },
            };
            cell.font = { color: { argb: negro } };
            cell.alignment = { vertical: "middle", horizontal: "left" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        formatColumnByHeader(sheet, "precio_cliente", '"$"#,##0.00');
        formatColumnByHeader(sheet, "descuento", '0.00"%"');
        sheet.addRow([]);

        if (payload.totales && payload.discount !== undefined) {
          const totalCobros = rows.reduce(
            (acc, row) => acc + (parseFloat(row.total) || 0),
            0
          );

          const netoFinal = payload.totales.netoFinal;
          const netoMenosCobros = netoFinal - totalCobros;

          const resumen = [
            ["Resumen de Envíos"],
            ["Total de envíos", payload.totales.totalEnvios],
            ["Monto bruto", `$${payload.totales.montoTotal.toFixed(2)}`],
          ];

          if (payload.discount > 0) {
            resumen.push([
              "Descuento (%)",
              `${(payload.discount * 100).toFixed(2)}%`,
            ]);
          }

          resumen.push(["Neto final", `$${netoFinal.toFixed(2)}`]);
          resumen.push([
            "Total cobros a descontar",
            `$${totalCobros.toFixed(2)}`,
          ]);

          const etiquetaFinal =
            netoMenosCobros >= 0 ? "Total a abonar" : "Total a rendir";

          resumen.push([
            etiquetaFinal,
            `$${Math.abs(netoMenosCobros).toFixed(2)}`,
          ]);

          resumen.forEach(([label, value], index) => {
            const row = sheet.addRow([label, value]);

            row.getCell(1).font = { bold: true, color: { argb: negro } };
            row.getCell(2).font = {
              bold: index >= resumen.length - 1,
              color: { argb: index >= resumen.length - 1 ? dorado : negro },
            };

            row.eachCell((cell) => {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: blanco },
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
              cell.alignment = { vertical: "middle", horizontal: "left" };
            });
          });
        }

        sheet.columns.forEach((col) => {
          let max = 10;

          col.eachCell?.((cell) => {
            const len = cell.value?.toString().length || 0;
            if (len > max) max = len;
          });

          col.width = max + 2;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        zip.file(`Detalle_${nombre}_${formattedDate}.xlsx`, buffer);
      } catch (err) {
        console.error(`❌ Error con ${nombre}:`, err);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");

      link.href = URL.createObjectURL(zipBlob);
      link.download = `envios_todos_las_tiendas_${formattedDate}.zip`;
      link.click();
    } catch (zipErr) {
      console.error("❌ Error generando ZIP:", zipErr);
      alert("Ocurrió un error al generar el ZIP");
    }
  };

  const handleDownloadAllDrivers = async () => {
    if (!token) {
      alert("❌ Sesión inválida. Volvé a iniciar sesión.");
      return;
    }

    if (!dataCadetes.length) return alert("No hay datos para exportar");

    const zip = new JSZip();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const dd = String(yesterday.getDate()).padStart(2, "0");
    const mm = String(yesterday.getMonth() + 1).padStart(2, "0");
    const yyyy = yesterday.getFullYear();
    const formattedDate = `${dd}-${mm}-${yyyy}`;

    for (const cadete of dataCadetes) {
      const nombre = cadete.chofer;

      try {
        const res = await authFetch("/v1/data/driver/me", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({ username: nombre }),
        });

        const payload = await res.json();

        if (!res.ok || !payload.result?.length) {
          console.warn(`⚠️ Sin datos para: ${nombre}`);
          continue;
        }

        const rows = payload.result;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Envios");

        const pastelHeader = "FFDEF7E5";
        const pastelBody = "FFF0F4F8";
        const dorado = "FFFFC300";
        const blanco = "FFFFFFFF";
        const negro = "FF000000";

        function formatColumnByHeader(sheet, headerText, format) {
          const headerRow = sheet.getRow(1);

          headerRow.eachCell((cell, colNumber) => {
            if (cell.value === headerText) {
              sheet.getColumn(colNumber).eachCell((c, rowNumber) => {
                if (rowNumber > 1 && typeof c.value === "number") {
                  c.numFmt = format;
                }
              });
            }
          });
        }

        const headers = Object.keys(rows[0]);
        const headersRow = sheet.addRow(headers);

        headersRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: negro } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: pastelHeader },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        rows.forEach((row) => {
          const rowData = headers.map((h) => {
            if (h === "precio_chofer") return Number(row[h]);
            if (h === "porcentaje_chofer") return Number(row[h]);
            return row[h];
          });

          const newRow = sheet.addRow(rowData);

          newRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: pastelBody },
            };
            cell.font = { color: { argb: negro } };
            cell.alignment = { vertical: "middle", horizontal: "left" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        formatColumnByHeader(sheet, "precio_chofer", '"$"#,##0.00');
        formatColumnByHeader(sheet, "porcentaje_chofer", '0.00"%"');
        sheet.addRow([]);

        if (payload.totales && payload.discount !== undefined) {
          const resumen = [
            ["Resumen de Envíos"],
            ["Total de envíos", payload.totales.totalEnvios],
            ["Monto bruto", `$${payload.totales.montoTotal.toFixed(2)}`],
            ["Descuento (%)", `${(payload.discount * 100).toFixed(2)}%`],
            ["Neto final a cobrar", `$${payload.totales.netoFinal.toFixed(2)}`],
          ];

          resumen.forEach(([label, value], index) => {
            const row = sheet.addRow([label, value]);

            row.getCell(1).font = { bold: true, color: { argb: negro } };
            row.getCell(2).font = {
              bold: index === 4,
              color: { argb: index === 4 ? dorado : negro },
            };

            row.eachCell((cell) => {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: blanco },
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
              cell.alignment = { vertical: "middle", horizontal: "left" };
            });
          });
        }

        sheet.columns.forEach((col) => {
          let max = 10;

          col.eachCell?.((cell) => {
            const len = cell.value?.toString().length || 0;
            if (len > max) max = len;
          });

          col.width = max + 2;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        zip.file(`Detalle_${nombre}_${formattedDate}.xlsx`, buffer);
      } catch (err) {
        console.error(`❌ Error con ${nombre}:`, err);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");

      link.href = URL.createObjectURL(zipBlob);
      link.download = `envios_todos_los_choferes_${formattedDate}.zip`;
      link.click();
    } catch (zipErr) {
      console.error("❌ Error generando ZIP:", zipErr);
      alert("Ocurrió un error al generar el ZIP");
    }
  };

  const handleDownloadIndividual = async (nombre, tipo) => {
    if (!token) {
      alert("❌ Sesión inválida. Volvé a iniciar sesión.");
      return;
    }

    try {
      const endpoint =
        tipo === "cadete" ? "/v1/data/driver/me" : "/v1/data/client/me";

      console.log("📦 Descargando archivo individual:", {
        nombre,
        tipo,
        endpoint,
      });

      const res = await authFetch(endpoint, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ username: nombre?.trim() }),
      });

      const payload = await res.json();

      if (!res.ok) throw new Error(payload.error || "Error al obtener datos");

      const rows = payload.result || [];

      console.log("👉 Datos del primer envío:", rows[0]);

      if (!rows.length) return alert("❌ No hay datos para exportar");

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Envios");

      const pastelHeader = "FFDEF7E5";
      const pastelBody = "FFF0F4F8";
      const dorado = "FFFFC300";
      const blanco = "FFFFFFFF";
      const negro = "FF000000";

      function formatColumnByHeader(sheet, headerText, format) {
        const headerRow = sheet.getRow(1);

        headerRow.eachCell((cell, colNumber) => {
          if (cell.value === headerText) {
            sheet.getColumn(colNumber).eachCell((c, rowNumber) => {
              if (rowNumber > 1 && typeof c.value === "number") {
                c.numFmt = format;
              }
            });
          }
        });
      }

      const headers = Object.keys(rows[0]);
      const headersRow = sheet.addRow(headers);

      headersRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: negro } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: pastelHeader },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      rows.forEach((row) => {
        const rowData = headers.map((h) => {
          if (h === "precio_cliente") return Number(row[h]);
          if (h === "descuento") return Number(row[h]);
          if (h === "precio_chofer") return Number(row[h]);
          if (h === "porcentaje_chofer") return Number(row[h]);
          return row[h];
        });

        const newRow = sheet.addRow(rowData);

        newRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: pastelBody },
          };
          cell.font = { color: { argb: negro } };
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      if (tipo === "cadete") {
        formatColumnByHeader(sheet, "precio_chofer", '"$"#,##0.00');
        formatColumnByHeader(sheet, "porcentaje_chofer", '0.00"%"');
      } else if (tipo === "cliente") {
        formatColumnByHeader(sheet, "precio_cliente", '"$"#,##0.00');
        formatColumnByHeader(sheet, "descuento", '0.00"%"');
      }

      sheet.addRow([]);

      if (payload.totales && payload.discount !== undefined) {
        const resumen = [
          ["Resumen de Envíos"],
          ["Total de envíos", payload.totales.totalEnvios],
          ["Monto bruto", `$${payload.totales.montoTotal.toFixed(2)}`],
          ["Descuento (%)", `${(payload.discount * 100).toFixed(2)}%`],
          ["Neto final a cobrar", `$${payload.totales.netoFinal.toFixed(2)}`],
        ];

        resumen.forEach(([label, value], index) => {
          const row = sheet.addRow([label, value]);

          row.getCell(1).font = { bold: true, color: { argb: negro } };
          row.getCell(2).font = {
            bold: index === 4,
            color: { argb: index === 4 ? dorado : negro },
          };

          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: blanco },
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            cell.alignment = { vertical: "middle", horizontal: "left" };
          });
        });
      }

      sheet.columns.forEach((col) => {
        let max = 10;

        col.eachCell?.((cell) => {
          const len = cell.value?.toString().length || 0;
          if (len > max) max = len;
        });

        col.width = max + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);
      link.download = `envios_${tipo}_${nombre}.xlsx`;
      link.click();
    } catch (err) {
      console.error("❌ Error en handleDownloadIndividual:", err);
      alert("❌ Error exportando archivo");
    }
  };

  const toNumber = (value) => {
    if (typeof value === "number") return value;

    if (typeof value === "string") {
      const normalized = value.replace(/\./g, "").replace(",", ".");
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  };

  const currencyFormatter = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value);

  const choferesCount = dataCadetes.length;
  const tiendasCount = dataClientes.length;

  const totalSemanalChoferes = useMemo(() => {
    return dataCadetes.reduce((acc, item) => acc + toNumber(item.semanal), 0);
  }, [dataCadetes]);

  const totalParcialChoferes = useMemo(() => {
    return dataCadetes.reduce((acc, item) => acc + toNumber(item.parcial), 0);
  }, [dataCadetes]);

  const totalSemanalTiendas = useMemo(() => {
    return dataClientes.reduce((acc, item) => acc + toNumber(item.semanal), 0);
  }, [dataClientes]);

  const pageWrapStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  };

  const headerRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  };

  const titleStyle = {
    margin: 0,
    fontSize: "34px",
    fontWeight: 800,
    color: "var(--text-primary)",
    letterSpacing: "-0.03em",
  };

  const subtitleStyle = {
    margin: "8px 0 0 0",
    color: "var(--text-secondary)",
    fontSize: "15px",
    lineHeight: 1.6,
  };

  const actionsWrapStyle = {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  };

  const kpiGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "16px",
  };

  const kpiLabelStyle = {
    fontSize: "13px",
    color: "var(--text-secondary)",
    marginBottom: "10px",
  };

  const kpiValueStyle = {
    fontSize: "28px",
    fontWeight: 800,
    color: "var(--text-primary)",
    letterSpacing: "-0.03em",
  };

  const kpiMetaStyle = {
    marginTop: "10px",
    fontSize: "13px",
    color: "var(--text-secondary)",
  };

  const tablesGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  };

  const cardHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "14px",
    flexWrap: "wrap",
  };

  const sectionTitleStyle = {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: "var(--text-primary)",
  };

  const sectionTextStyle = {
    margin: "6px 0 0 0",
    color: "var(--text-secondary)",
    fontSize: "14px",
  };

  const tableWrapStyle = {
    overflowX: "auto",
    border: "1px solid var(--border)",
    borderRadius: "18px",
    background: "rgba(255,255,255,0.02)",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle = {
    textAlign: "left",
    padding: "14px 12px",
    color: "var(--text-secondary)",
    fontSize: "13px",
    fontWeight: 700,
    borderBottom: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const tdStyle = {
    padding: "14px 12px",
    color: "var(--text-primary)",
    fontSize: "14px",
    borderBottom: "1px solid var(--border)",
    verticalAlign: "middle",
  };

  const iconButtonStyle = {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "12px",
    minWidth: "38px",
    minHeight: "38px",
    cursor: "pointer",
    color: "var(--text-primary)",
    fontSize: "16px",
  };

  const isLoaded = dataCadetes.length > 0 || dataClientes.length > 0;

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Totales</h1>
          <p style={subtitleStyle}>
            Resumen general de choferes y tiendas, con exportaciones individuales y masivas.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button onClick={dataRequest}>Ver totales</Button>

          <Button variant="secondary" onClick={handleDownloadExcel} disabled={!isLoaded}>
            Descargar Excel
          </Button>

          <Button variant="secondary" onClick={handleDownloadAllDrivers}>
            ZIP choferes
          </Button>

          <Button variant="secondary" onClick={handleDownloadAllClients}>
            ZIP tiendas
          </Button>

          <Button variant="danger" onClick={resetDatabase}>
            Reiniciar base
          </Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="totales-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Choferes con datos</div>
          <div style={kpiValueStyle}>{choferesCount}</div>
          <div style={kpiMetaStyle}>Cantidad de choferes en el resumen actual.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Tiendas con datos</div>
          <div style={kpiValueStyle}>{tiendasCount}</div>
          <div style={kpiMetaStyle}>Cantidad de tiendas con resumen disponible.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Semanal choferes</div>
          <div style={kpiValueStyle}>{currencyFormatter(totalSemanalChoferes)}</div>
          <div style={kpiMetaStyle}>Suma total del campo semanal de choferes.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Semanal tiendas</div>
          <div style={kpiValueStyle}>{currencyFormatter(totalSemanalTiendas)}</div>
          <div style={kpiMetaStyle}>Suma total del campo semanal de tiendas.</div>
        </Card>
      </div>

      <div style={tablesGridStyle} className="totales-tables-grid">
        <Card>
          <div style={cardHeaderStyle}>
            <div>
              <h2 style={sectionTitleStyle}>Totales choferes</h2>
              <p style={sectionTextStyle}>
                Resumen por conductor con descarga individual.
              </p>
            </div>

            <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
              Parcial total: {currencyFormatter(totalParcialChoferes)}
            </div>
          </div>

          {dataCadetes.length > 0 ? (
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(dataCadetes[0]).map((key, i) => (
                      <th key={i} style={thStyle}>
                        {key}
                      </th>
                    ))}
                    <th style={thStyle}>Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {dataCadetes.map((row, i) => (
                    <tr key={i}>
                      {Object.keys(dataCadetes[0]).map((key, j) => (
                        <td key={j} style={tdStyle}>
                          {row[key]}
                        </td>
                      ))}
                      <td style={tdStyle}>
                        <button
                          onClick={() =>
                            handleDownloadIndividual(
                              row?.chofer ?? row?.nombre ?? row?.username ?? "",
                              "cadete"
                            )
                          }
                          style={iconButtonStyle}
                          title="Descargar detalle"
                        >
                          ⬇
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Todavía no hay datos cargados para choferes.
            </div>
          )}
        </Card>

        <Card>
          <div style={cardHeaderStyle}>
            <div>
              <h2 style={sectionTitleStyle}>Totales tiendas</h2>
              <p style={sectionTextStyle}>
                Resumen por tienda con descarga individual.
              </p>
            </div>

            <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
              Exportación masiva disponible en ZIP.
            </div>
          </div>

          {dataClientes.length > 0 ? (
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(dataClientes[0]).map((key, i) => (
                      <th key={i} style={thStyle}>
                        {key}
                      </th>
                    ))}
                    <th style={thStyle}>Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {dataClientes.map((row, i) => (
                    <tr key={i}>
                      {Object.keys(dataClientes[0]).map((key, j) => (
                        <td key={j} style={tdStyle}>
                          {row[key]}
                        </td>
                      ))}
                      <td style={tdStyle}>
                        <button
                          onClick={() => {
                            console.log("🤔 Tienda:", row);
                            handleDownloadIndividual(row.cliente, "cliente");
                          }}
                          style={iconButtonStyle}
                          title="Descargar detalle"
                        >
                          ⬇
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Todavía no hay datos cargados para tiendas.
            </div>
          )}
        </Card>
      </div>

      <style>
        {`
          .totales-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .totales-tables-grid {
            grid-template-columns: 1fr 1fr;
          }

          @media (max-width: 1180px) {
            .totales-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .totales-tables-grid {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 720px) {
            .totales-kpi-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Totales;